import { DocumentType } from '@erp/shared-types';
import { msg } from 'gt-react';

/**
 * Document types that are valid AI classification targets / pickable in
 * workflow node configs. Excludes the two internal markers:
 *   - OTHER             — fallback bucket the AI assigns when nothing matches.
 *   - MULTI_DOC_PARENT  — applied automatically after a successful split.
 *
 * Mirrors the filter used by `DocumentClassificationService` (backend) and
 * `DocumentSplittingService` (backend) so frontend pickers and backend logic
 * stay in lockstep.
 */
export type ClassifiableDocumentType = Exclude<
  DocumentType,
  DocumentType.OTHER | DocumentType.MULTI_DOC_PARENT
>;

/**
 * UI-facing metadata for a single `DocumentType`.
 *
 * `label` is encoded with `msg(...)`, so consumers must decode it through
 * the `m` function returned by `useMessages()` (or the `mFallback` helper
 * from gt-react when not inside a React component). Storing labels encoded
 * here keeps i18n extraction working from this single file.
 */
export interface DocumentTypeMetadata {
  label: string;
  /** Whether this type is user-pickable / a real AI classification target. */
  isClassifiable: boolean;
}

/**
 * Single source of truth for `DocumentType` UI labels.
 *
 * The `Record<DocumentType, …>` type forces TypeScript to fail compilation
 * if a new enum member is added to `@erp/shared-types/DocumentTypes.ts`
 * without metadata here — so every UI surface that consumes this map picks
 * up the new type automatically (modulo a `pnpm turbo i18n:gen` run for the
 * new label string).
 *
 * To add display fields like `description`, `color`, or `iconName`, extend
 * `DocumentTypeMetadata` and fill in every entry below.
 */
export const DOCUMENT_TYPE_METADATA: Record<
  DocumentType,
  DocumentTypeMetadata
> = {
  [DocumentType.PURCHASE_ORDER]: {
    label: msg('Purchase Order', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.AP_INVOICE]: {
    label: msg('AP Invoice', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.AP_INVOICE_UPDATE]: {
    label: msg('AP Invoice Update', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.REMITTANCE_ADVICE]: {
    label: msg('Remittance Advice', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.LEAD_TIME_SHEET]: {
    label: msg('Lead Time Sheet', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.CARRIER_CONFIRMATION]: {
    label: msg('Carrier Confirmation', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.BILL_OF_LADING]: {
    label: msg('Bill of Lading', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.PROOF_OF_DELIVERY]: {
    label: msg('Proof of Delivery', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.RECEIVING_STAMP]: {
    label: msg('Receiving Stamp', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.RECEIPT]: {
    label: msg('Receipt', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.DISCOUNT_REQUEST]: {
    label: msg('Discount Request', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.IM_PRICELISTS]: {
    label: msg('IM Pricelist', { context: 'document type' }),
    isClassifiable: false,
  },
  [DocumentType.PRICING_SPA]: {
    label: msg('SPA Price File', { context: 'document type' }),
    isClassifiable: true,
  },
  [DocumentType.CSV_CONVERSION]: {
    label: msg('CSV Conversion', { context: 'document type' }),
    // Stamped by the converter utility, never routed through classification.
    isClassifiable: false,
  },
  [DocumentType.EDI_GUIDELINE]: {
    label: msg('EDI Guideline', { context: 'document type' }),
    // Stamped by the guideline upload, never routed through classification.
    isClassifiable: false,
  },
  [DocumentType.OTHER]: {
    label: msg('Other', { context: 'document type fallback' }),
    isClassifiable: false,
  },
  [DocumentType.MULTI_DOC_PARENT]: {
    label: msg('Multi-document container', {
      context: 'document type internal marker for a split parent',
    }),
    isClassifiable: false,
  },
};

export function isClassifiableDocumentType(
  value: DocumentType,
): value is ClassifiableDocumentType {
  return DOCUMENT_TYPE_METADATA[value].isClassifiable;
}

/**
 * All classifiable types in enum-declaration order. Use this as the source
 * for any "pick a document type" UI — the order will pick up new types
 * automatically based on where they're declared in `DocumentTypes.ts`.
 */
export const CLASSIFIABLE_DOCUMENT_TYPES: ClassifiableDocumentType[] = (
  Object.values(DocumentType) as DocumentType[]
).filter(isClassifiableDocumentType);
